import { api } from "@/lib/api/client";
import type { CallState } from "@/lib/doorbell-call/protocol";

/** `GET /doorbell/calls/{id}` — what the call page reads before it opens the call socket. */
export interface DoorbellCallInfo {
  state: CallState;
  /** "Anna Svensson" or "anyone at Acme AB". */
  asking_for: string;
  /** Where the visitor is, e.g. "Main entrance, L26"; "" when unknown (show "reception"). */
  location: string;
  answered_by_name: string | null;
  expires_at: string;
  /** The tablet has an entrance door: show Open door once connected. */
  door_available: boolean;
  /** The guide's "Ring me" call. */
  is_test: boolean;
  /** The company's ring time (30 for a test call). */
  ring_seconds: number;
  /** This member declined it: the call is gone for them, never offered again. */
  declined_by_you: boolean;
}

/** The company's live-call settings (`crm.Tenant` columns; company admins). */
export interface DoorbellCompanySettings {
  doorbell_calls_enabled: boolean;
  doorbell_person_selection: boolean;
  doorbell_ring_seconds: number;
}

/** `GET /doorbell/calls/{id}/ice` — STUN/TURN for this call (Assumption A4). */
export interface IceResponse {
  ice_servers?: RTCIceServer[];
  iceServers?: RTCIceServer[];
}

export const doorbellCallKey = (callId: number) => ["doorbell-call", callId] as const;
export const DOORBELL_COMPANY_SETTINGS_KEY = ["doorbell-company-settings"] as const;

export const doorbellCallsApi = {
  get: (callId: number, config?: { signal?: AbortSignal }) => api.get<DoorbellCallInfo>(`/doorbell/calls/${callId}`, config),
  /** Only a rung person (`CallTarget`) may decline. */
  decline: (callId: number) => api.post(`/doorbell/calls/${callId}/decline`),
  /** `200 {ok: true}`, or `422 {message}` when the door can't be opened. */
  openDoor: (callId: number) => api.post<{ ok: true }>(`/doorbell/calls/${callId}/open-door`),
  /** The tablet or the member who answered only. */
  ice: (callId: number) => api.get<IceResponse>(`/doorbell/calls/${callId}/ice`),
  /** The guide's test call to the company admin themself: `201 {call_id, ring_seconds}`. */
  startTest: () => api.post<{ call_id: number; ring_seconds: number }>("/doorbell/calls/test/"),
  /** GET for members of the company; PUT for company admins (403 otherwise). */
  getCompanySettings: () => api.get<DoorbellCompanySettings>("/doorbell/company-settings/"),
  saveCompanySettings: (settings: DoorbellCompanySettings) =>
    api.put<DoorbellCompanySettings>("/doorbell/company-settings/", settings),
};

export function iceServersFrom(body: IceResponse | null | undefined): RTCIceServer[] {
  const list = body?.ice_servers ?? body?.iceServers ?? [];
  return list.filter((s) => !!s && (typeof s.urls === "string" || Array.isArray(s.urls)));
}

/** The server's `message` (422 from open-door) or `detail` (HttpError), else `fallback`. */
export function callApiError(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { message?: unknown; detail?: unknown } } } | null)?.response?.data;
  if (typeof data?.message === "string" && data.message) return data.message;
  if (typeof data?.detail === "string" && data.detail) return data.detail;
  return fallback;
}

/** A 403 `{code: "not_a_target"}` from `/decline` — this member was never one
 *  of the call's targets, so the call page hides the Decline button instead
 *  of showing an error. */
export function isNotATargetError(err: unknown): boolean {
  const resp = (err as { response?: { status?: unknown; data?: { code?: unknown } } } | null)?.response;
  return resp?.status === 403 && resp.data?.code === "not_a_target";
}
